/* eslint-disable monorepo/no-internal-import */
import {HydrationBoundary} from '@aboutyou/ui-components/src/hooks/useHydrationDone/useHydrationDone';
import React, {ComponentType} from 'react';
import {useAddToResourceHints} from './ResourceHintsProvider';

export function lazy<T extends ComponentType<any>>(
  importer: () => Promise<{default: T}>,
  config?: {
    /**
     * If true, the lazy-loaded module will be optimized for preloading as a priority resource.
     * Usually, this is only necessary for main Page/Route components.
     * Other modules should be downloaded lazily by react on demand when hydrating the page.
     */
    aboveTheFold?: boolean;
  } & (
    | {
        /**
         * The fallback component to show while the lazy-loaded module is loading.
         * If not provided, the Suspense Boundary will render nothing until the module is loaded.
         */
        fallback?: NonNullable<React.ReactNode> | null;
      }
    | {
        /**
         * If true, the Suspense Boundary will be skipped and the module will still be loaded lazily but would not suspend the rendering.
         */
        skipSuspense?: true;
      }
  ),
) {
  const LazyLoaded = React.lazy(importer);

  const Suspensified = React.forwardRef(function Inner(props, ref) {
    if (!process.browser && config?.aboveTheFold) {
      // eslint-disable-next-line react-hooks/rules-of-hooks -- its okay break the rules here because the config.aboveTheFold is a constant, would never change for a given component
      useAddToResourceHints(importer.toString());
    }

    if (config && 'skipSuspense' in config && config.skipSuspense) {
      return <LazyLoaded {...(props as any)} ref={ref} />;
    }

    if (!process.browser) {
      return (
        <React.Suspense fallback={null}>
          <LazyLoaded {...(props as any)} ref={ref} />
        </React.Suspense>
      );
    }

    return (
      <React.Suspense
        fallback={config && 'fallback' in config ? config.fallback : null}
      >
        <HydrationBoundary>
          <LazyLoaded {...(props as any)} ref={ref} />
        </HydrationBoundary>
      </React.Suspense>
    );
  });

  // @ts-expect-error -- adding a custom property to react component type
  Suspensified.preload = importer;

  return Suspensified as unknown as T & {preload: () => Promise<void>};
}
